import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import DataConnector from 'connect/DataConnector';
import {
  ConjugateGraphQL,
  OligoConjugateVersionGraphQL,
  OligoDuplexOligoStrandGraphQL,
  OligoDuplexVersionGraphQL,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {isOligoConjugateVersion} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {ComplexPolymerStructure} from 'modbio/complex-polymers/tabs/ComplexPolymerComponentsTableTab';
import {ModifiedNucleotidesPreferences, NucleotideComponent} from 'molbio/monomers/MonomerConstants';
import {
  MonomersMap,
  MonomerQueryMonomer,
  ALLOWED_PAGE_SIZES,
  NUCLEOTIDES_COLUMNS,
  Cell,
  ROW_HEIGHT,
  getRowsData,
  getNucleotideTableRowData,
} from 'molbio/monomers/NucleotidesTable';
import {updateNucleotidesTablePaginationOffset} from 'molbio/monomers/NucleotidesUIReduxActions';
import ListingSettingsDropdown from 'nav/ListingSettingsDropdown';
import usePreference from 'ui-library/hooks/usePreference';
import ResultsPager from 'ui-library/ResultsPager';
import ScrollableTable, {DEFAULT_TABLE_COLUMN_WIDTH} from 'ui-library/scrollable/ScrollableTable';
import {ScrollableTableRow} from 'ui-library/scrollable/ScrollableTableConstants';

import {
  getConjugatesDiplayWidth,
  getConjugatesDisplayHeight,
} from './ComplexPolymerComponentsTableConjugateStructureUtil';
import ComplexPolymerConjugateStructure from './ComplexPolymerConjugateStructure';
import {
  getOligoDuplexTotalLength,
  getOligoStrandLeftPadding,
  getReverseAdjustedDisplayResidueIdx,
} from './ComplexPolymerMapUtil';

interface TableParams {
  monomersById: MonomersMap;
  rowOffset: number;
  pageSize: number;
}

function getConjugatesDisplay(conjugates: Array<ConjugateGraphQL> | null): JSX.Element | string {
  if (conjugates == null || conjugates.length === 0) {
    return '-';
  }
  return <ComplexPolymerConjugateStructure conjugates={conjugates} />;
}

function getOligoConjugateRows(
  complexPolymerStructure: OligoConjugateVersionGraphQL,
  rowParams: TableParams
): Array<ScrollableTableRow> {
  const {bases, nucleotideType, modifiedNucleotides} = complexPolymerStructure.oligo!;
  const conjugates = complexPolymerStructure.conjugates;
  const {rowOffset, pageSize, monomersById} = rowParams;

  const cellProps = {
    editable: false,
    openMenu: null,
    setOpenMenu: noop,
    onUpdateMod: noop,
    monomersById,
    sequenceLength: bases.length,
    nucleotideType,
  };

  const rowsData = getRowsData(bases, modifiedNucleotides, nucleotideType, rowOffset, pageSize, monomersById);

  const rows = rowsData.map((row, i) => {
    const conjugatesAtResidue =
      conjugates?.filter((conjugate) => conjugate.residueIndex === rowOffset + i) ?? [];

    return {
      id: row.id,
      data: {
        position: row.data.position,
        displayPosition: row.data.displayPosition,
        height: conjugatesAtResidue.length > 0 ? getConjugatesDisplayHeight(conjugatesAtResidue) : ROW_HEIGHT,
        sugar: <Cell nucleotideComponent={NucleotideComponent.SUGAR} rowData={row.data} {...cellProps} />,
        phosphate: (
          <Cell nucleotideComponent={NucleotideComponent.PHOSPHATE} rowData={row.data} {...cellProps} />
        ),
        base: <Cell nucleotideComponent={NucleotideComponent.BASE} rowData={row.data} {...cellProps} />,
        conjugates: getConjugatesDisplay(conjugatesAtResidue),
        conjugatesWidth:
          conjugatesAtResidue.length > 0
            ? getConjugatesDiplayWidth(conjugatesAtResidue)
            : DEFAULT_TABLE_COLUMN_WIDTH,
      },
    };
  });

  return rows;
}

function getOligoStrandRow(
  rowIdx: number,
  duplexPolymer: OligoDuplexOligoStrandGraphQL,
  offset: number,
  duplexLength: number,
  isForward: boolean,
  rowParams: TableParams
): ScrollableTableRow | null {
  const {monomersById} = rowParams;
  const {bases, modifiedNucleotides, nucleotideType} = duplexPolymer.oligo;

  const leftPad = getOligoStrandLeftPadding(offset, isForward);
  const rightPad = duplexLength - duplexPolymer.oligo.bases.length - leftPad;

  if (rowIdx < leftPad || rowIdx >= duplexLength - rightPad) {
    return null;
  }

  const residueIdx = rowIdx - leftPad;
  const base = bases[residueIdx];
  const modifiedNucleotide = modifiedNucleotides.find((mn) => mn.position === residueIdx) || null;

  const nucleotideData = getNucleotideTableRowData(
    base,
    getReverseAdjustedDisplayResidueIdx(residueIdx, bases.length, isForward),
    modifiedNucleotide,
    monomersById,
    nucleotideType
  ).data;

  const defaultCellProps = {
    editable: false,
    openMenu: null,
    setOpenMenu: noop,
    onUpdateMod: noop,
    monomersById: rowParams.monomersById,
    sequenceLength: duplexPolymer.oligo.bases.length,
    nucleotideType: duplexPolymer.oligo.nucleotideType,
  };

  const conjugates =
    duplexPolymer.conjugates.filter((conjugate) => conjugate.residueIndex === residueIdx) ?? [];

  const row = {
    id: `${isForward ? 'F' : 'R'}${nucleotideData.position.toString()}`,
    data: {
      position: rowIdx,
      displayPosition: (
        <div>
          {isForward ? 'F' : 'R'}-{nucleotideData.position}
        </div>
      ),
      height: conjugates.length > 0 ? getConjugatesDisplayHeight(conjugates) : ROW_HEIGHT,
      sugar: (
        <Cell
          nucleotideComponent={NucleotideComponent.SUGAR}
          rowData={nucleotideData}
          {...defaultCellProps}
        />
      ),
      phosphate: (
        <Cell
          nucleotideComponent={NucleotideComponent.PHOSPHATE}
          rowData={nucleotideData}
          {...defaultCellProps}
        />
      ),
      base: (
        <Cell nucleotideComponent={NucleotideComponent.BASE} rowData={nucleotideData} {...defaultCellProps} />
      ),
      conjugates: getConjugatesDisplay(conjugates),
      conjugatesWidth:
        conjugates.length > 0 ? getConjugatesDiplayWidth(conjugates) : DEFAULT_TABLE_COLUMN_WIDTH,
    },
  };

  return row;
}

function getOligoDuplexRows(
  complexPolymerStructure: OligoDuplexVersionGraphQL,
  rowParams: TableParams
): Array<ScrollableTableRow> {
  const {rowOffset, pageSize} = rowParams;

  const rows: Array<ScrollableTableRow> = [];
  const duplexLength = getOligoDuplexTotalLength(complexPolymerStructure);

  _.range(rowOffset, Math.min(rowOffset + pageSize, duplexLength)).forEach((rowIdx) => {
    const forwardRow = getOligoStrandRow(
      rowIdx,
      complexPolymerStructure.forwardStrand!,
      complexPolymerStructure.offset!,
      duplexLength,
      true,
      rowParams
    );
    const reverseRow = getOligoStrandRow(
      rowIdx,
      complexPolymerStructure.reverseStrand!,
      complexPolymerStructure.offset!,
      duplexLength,
      false,
      rowParams
    );
    const forwardRowClassName = reverseRow ? css(styles.topCell) : undefined;
    const forwardClassNames = {
      className: forwardRowClassName,
      classNames: {
        displayPosition: forwardRowClassName,
        sugar: forwardRowClassName,
        phosphate: forwardRowClassName,
        base: forwardRowClassName,
        conjugates: forwardRowClassName,
      },
    };
    if (forwardRow) {
      rows.push({...forwardRow, ...forwardClassNames});
    }
    if (reverseRow) {
      rows.push(reverseRow);
    }
  });

  return rows;
}

export function getComplexPolymerRows(
  complexPolymerStructure: ComplexPolymerStructure,
  rowParams: TableParams
): Array<ScrollableTableRow> {
  if (isOligoConjugateVersion(complexPolymerStructure)) {
    return getOligoConjugateRows(complexPolymerStructure, rowParams);
  }

  return getOligoDuplexRows(complexPolymerStructure, rowParams);
}

export function getTotalTableHeight(complexPolymerStructure: ComplexPolymerStructure): number {
  if (isOligoConjugateVersion(complexPolymerStructure)) {
    return complexPolymerStructure.oligo!.bases.length;
  }

  return getOligoDuplexTotalLength(complexPolymerStructure);
}

export interface Props {
  complexPolymerStructure: ComplexPolymerStructure;
  complexPolymerId: string;
  monomers: Array<MonomerQueryMonomer>;
}

interface EnhancedProps {
  offset: number;
  setOffset: (offset: number) => void;
}

export type ComplexPolymerComponentsTableProps = Props & EnhancedProps;

class ComplexPolymerComponentsTableConnector extends DataConnector<Props, EnhancedProps> {
  constructor(props: Props) {
    super(props);
  }

  enhanceProps(): EnhancedProps {
    const state = this.reduxState().ui.molbio.nucleotidesUI[this.props.complexPolymerId];
    return {
      offset: state?.tablePaginationOffset ?? 0,
      setOffset: (offset: number) => {
        this.dispatch(updateNucleotidesTablePaginationOffset(this.props.complexPolymerId, offset));
      },
    };
  }
}

function ComplexPolymerComponentsTable({
  complexPolymerStructure,
  monomers,
  offset,
  setOffset,
}: Props & EnhancedProps): JSX.Element {
  const [pageSize, setPageSize] = usePreference<number>(
    ModifiedNucleotidesPreferences.NUCLEOTIDES_TABLE_PAGE_SIZE
  );

  const monomersById: MonomersMap = {};
  for (const monomer of monomers) {
    monomersById[monomer.id] = monomer;
  }

  const rows = getComplexPolymerRows(complexPolymerStructure, {monomersById, rowOffset: offset, pageSize});

  function getRowContentHeight(rowIdx: number): number {
    return rows[rowIdx].data.height;
  }

  const COMPLEX_POLYMER_COLUMNS = [
    ...NUCLEOTIDES_COLUMNS,
    {
      header: 'Conjugates',
      accessorKey: 'conjugates',
      flexGrow: 5,
      isCollapsible: false,
      isSortable: false,
      width: Math.max(...rows.map((row) => row.data.conjugatesWidth)),
    },
  ];

  return (
    <>
      <div className={css(styles.pageOptions)}>
        <div className={css(styles.pageOptions)}>
          <ResultsPager
            allowFastForwardAndBackward={true}
            isLoading={false}
            setOffset={setOffset}
            pagination={{limit: pageSize, offset, total: getTotalTableHeight(complexPolymerStructure)}}
          />
          <div className="u-input-line-height">
            <ListingSettingsDropdown
              pageSizeConfig={{
                availablePageSizes: ALLOWED_PAGE_SIZES,
                pageSize,
                onSetPageSize: (pageSize: number) => {
                  setOffset(0);
                  setPageSize(pageSize);
                },
              }}
            />
          </div>
        </div>
      </div>
      <ScrollableTable
        rowSelectionMode={null}
        columns={COMPLEX_POLYMER_COLUMNS}
        emptyTableText="This complex polymer does not contain any nucleotides."
        getRowContentHeight={getRowContentHeight}
        headerClassName={css(styles.textCell)}
        isCompact={true}
        rows={rows}
      />
    </>
  );
}

const styles = StyleSheet.create({
  pageOptions: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  textCell: {
    paddingLeft: '12px',
  },
  topCell: {
    borderBottom: 'none',
  },
});

export default ComplexPolymerComponentsTableConnector.connect(ComplexPolymerComponentsTable);
